/**
 * Subscription Benefits Custom Element
 * A web component for expandable subscription benefits toggle
 */
class SubscriptionBenefitsElement extends HTMLElement {
  constructor() {
    super();
    
    // Bind methods to maintain proper context
    this.handleToggle = this.handleToggle.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  static get observedAttributes() {
    return ['is-open'];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'is-open' && this.toggle && this.content) {
      this.updateExpandedState(newValue === 'true');
    }
  }

  init() {
    // Find elements within this custom element
    this.toggle = this.querySelector('[data-subscription-benefits-toggle]');
    this.content = this.querySelector('[data-subscription-benefits-content]');
    
    if (!this.toggle || !this.content) {
      console.warn('SubscriptionBenefitsElement: Required elements not found');
      return;
    }

    this.bindEvents();
    this.setInitialState();
  }

  bindEvents() {
    // Remove existing listeners to prevent duplicates
    this.toggle.removeEventListener('click', this.handleToggle);
    this.toggle.removeEventListener('keydown', this.handleKeydown);
    
    // Add event listeners
    this.toggle.addEventListener('click', this.handleToggle);
    this.toggle.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleToggle();
    }
  }

  setInitialState() {
    // Get initial state from attribute or toggle element
    const isOpen = this.getAttribute('is-open') === 'true' || 
                   this.toggle.getAttribute('aria-expanded') === 'true';
    
    this.updateExpandedState(isOpen);
  }

  handleToggle() {
    const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;
    
    this.updateExpandedState(newState);
    
    // Update custom element attribute
    this.setAttribute('is-open', newState.toString());
    
    // Smooth scroll to content when opening
    if (newState) {
      setTimeout(() => {
        this.content.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 100);
    }
    
    // Dispatch custom event for external listeners
    this.dispatchEvent(new CustomEvent('subscription-benefits:toggle', {
      detail: { expanded: newState },
      bubbles: true
    }));
  }

  updateExpandedState(expanded) {
    if (!this.toggle || !this.content) return;
    
    this.toggle.setAttribute('aria-expanded', expanded.toString());
    this.content.setAttribute('aria-hidden', (!expanded).toString());
  }

  // Public API methods
  expand() {
    this.setAttribute('is-open', 'true');
  }

  collapse() {
    this.setAttribute('is-open', 'false');
  }

  toggle() {
    const isOpen = this.getAttribute('is-open') === 'true';
    this.setAttribute('is-open', (!isOpen).toString());
  }

  get isExpanded() {
    return this.getAttribute('is-open') === 'true';
  }

  cleanup() {
    if (this.toggle) {
      this.toggle.removeEventListener('click', this.handleToggle);
      this.toggle.removeEventListener('keydown', this.handleKeydown);
    }
  }
}

// Register the custom element
if (!customElements.get('subscription-benefits')) {
  customElements.define('subscription-benefits', SubscriptionBenefitsElement);
}

// Legacy support - convert existing elements to custom elements
function upgradeExistingElements() {
  const legacyElements = document.querySelectorAll('.subscription-benefits:not(subscription-benefits)');
  legacyElements.forEach(element => {
    // Create new custom element
    const customElement = document.createElement('subscription-benefits');
    
    // Copy attributes
    Array.from(element.attributes).forEach(attr => {
      customElement.setAttribute(attr.name, attr.value);
    });
    
    // Move content
    customElement.innerHTML = element.innerHTML;
    
    // Replace in DOM
    element.parentNode.replaceChild(customElement, element);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', upgradeExistingElements);
} else {
  upgradeExistingElements();
}

// Handle Shopify section events for theme editor
document.addEventListener('shopify:section:load', function(event) {
  // Custom elements auto-initialize, but we may need to upgrade legacy elements
  setTimeout(() => {
    const legacyElements = event.target.querySelectorAll('.subscription-benefits:not(subscription-benefits)');
    legacyElements.forEach(element => {
      const customElement = document.createElement('subscription-benefits');
      Array.from(element.attributes).forEach(attr => {
        customElement.setAttribute(attr.name, attr.value);
      });
      customElement.innerHTML = element.innerHTML;
      element.parentNode.replaceChild(customElement, element);
    });
  }, 0);
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SubscriptionBenefitsElement;
}
